import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';
import './TfaEntryPage.css';

const CODE_LENGTH = 6;

export function TfaEntryPage() {
  const [recipientEmail, setRecipientEmail] = useState(() => {
    const loginName = localStorage.getItem('loginUserName')?.trim() || '';
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginName) ? loginName : '';
  });
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [resendNotice, setResendNotice] = useState('');
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const lastCheckedCode = useRef('');
  const navigate = useNavigate();

  useEffect(() => {
    const userId = localStorage.getItem('loginUserId')?.trim();
    const token = localStorage.getItem('token');
    if (!userId || !token) return;

    const controller = new AbortController();
    const loadRecipientEmail = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/user/full/${encodeURIComponent(userId)}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!response.ok) return;

        const user = await response.json();
        if (!controller.signal.aborted && typeof user?.email === 'string' && user.email.trim()) {
          setRecipientEmail(user.email.trim());
        }
      } catch {
        // Keep the login email, if available, when user data cannot be loaded.
      }
    };

    void loadRecipientEmail();
    return () => controller.abort();
  }, []);

  const sendAgain = () => {
    // TODO: Connect the resend API and its cooldown when the backend contract is available.
    setResendNotice('Das erneute Senden ist derzeit noch nicht verfügbar. Bitte wenden Sie sich an die Praxis.');
  };

  const check = (code: string) => {
    // TODO: Verify the completed two-factor authentication code.
    //void code;

    navigate('/dashboard'); // TODO temporary
  };

  const updateCode = (nextDigits: string[]) => {
    setDigits(nextDigits);
    const code = nextDigits.join('');

    if (code.length === CODE_LENGTH && code !== lastCheckedCode.current) {
      lastCheckedCode.current = code;
      check(code);
    } else if (code.length < CODE_LENGTH) {
      lastCheckedCode.current = '';
    }
  };

  const enterDigits = (value: string, startIndex: number) => {
    const enteredDigits = value.replace(/\D/g, '').slice(0, CODE_LENGTH - startIndex);
    if (!enteredDigits) return;

    const nextDigits = [...digits];
    enteredDigits.split('').forEach((digit, offset) => {
      nextDigits[startIndex + offset] = digit;
    });
    updateCode(nextDigits);

    const nextIndex = Math.min(startIndex + enteredDigits.length, CODE_LENGTH - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>, index: number) => {
    event.preventDefault();
    enterDigits(event.clipboardData.getData('text'), index);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <main
      className="tfa-entry-page"
      style={{ '--tfa-background-image': "url('/images/StartImage.jpg')" } as React.CSSProperties}
    >
      <section className="tfa-entry-page__card" aria-labelledby="tfa-heading">
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="tfa-entry-page__back"
          aria-label="Zurück zur Anmeldung"
        >
          <span aria-hidden="true" className="tfa-entry-page__back-arrow">
            ←
          </span>
          <span>Zurück</span>
        </button>

        <div className="tfa-entry-page__header">
          <div className="tfa-entry-page__badge">
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="tfa-entry-page__shield"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3.25c2.25 1.65 4.48 2.28 6.75 2.52v5.48c0 4.3-2.78 7.72-6.75 9.5-3.97-1.78-6.75-5.2-6.75-9.5V5.77C7.52 5.53 9.75 4.9 12 3.25Z"
              />
              <path strokeLinecap="round" strokeLinejoin="round" d="m9.25 11.9 1.78 1.78 3.72-3.86" />
            </svg>
          </div>

          <h1 id="tfa-heading" className="tfa-entry-page__heading">
            Zwei-Faktor-Verifizierung
          </h1>
          <p className="tfa-entry-page__description">
            Wir haben einen 6-stelligen Code an
            <br />
            <span className="tfa-entry-page__recipient">{recipientEmail || 'Ihre E-Mail-Adresse'}</span> gesendet. Bitte geben
            Sie ihn unten ein.
          </p>
        </div>

        <div className="tfa-entry-page__digits" role="group" aria-label="6-stelliger Verifizierungscode">
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(element) => {
                inputRefs.current[index] = element;
              }}
              value={digit}
              onChange={(event) => {
                const value = event.target.value.replace(/\D/g, '');
                if (!value) {
                  const nextDigits = [...digits];
                  nextDigits[index] = '';
                  updateCode(nextDigits);
                  return;
                }
                enterDigits(value, index);
              }}
              onPaste={(event) => handlePaste(event, index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              onFocus={(event) => event.currentTarget.select()}
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={CODE_LENGTH}
              aria-label={`Ziffer ${index + 1}`}
              className="tfa-entry-page__digit"
            />
          ))}
        </div>

        <aside className="tfa-entry-page__help" aria-labelledby="tfa-help-heading">
          <h2 id="tfa-help-heading" className="tfa-entry-page__help-heading">
            Keinen Code erhalten?
          </h2>
          <p className="tfa-entry-page__help-description">
            Prüfen Sie den Spam-Ordner oder kontrollieren Sie, ob die E-Mail-Adresse korrekt ist.
          </p>
          <button type="button" onClick={sendAgain} className="tfa-entry-page__resend">
            Code erneut senden
          </button>
          <p className="tfa-entry-page__notice" role="status">
            {resendNotice}
          </p>
        </aside>
      </section>
    </main>
  );
}
